import type { CapabilityDetailResponse } from "./capabilityTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { AdrLink } from "./AdrLink";
import { useApiResource } from "./useApiResource";

// CapabilityDetail renders the spec §9 capability detail page fields. A 404
// or 503 shows the backend's own message — never a fabricated empty detail
// standing in for missing data (same discipline as DashboardStats/
// CapabilityRegister).
//
// Since the 2026-08-27 register→issues migration (see CLAUDE.md), the
// volatile tier (current_status, current_surface, evidence, v0_parity,
// notes — frozen code-inspection claims, never re-verified since migration)
// is omitted from the API response entirely, per the ruling's "prefer
// omitting it": this page never renders them, and links to the source
// issue instead, where the disclaimer and any correction comments live.
export function CapabilityDetail({ token, id }: { token: string; id: string }) {
  const repo = useCurrentRepo();
  const { status, data: response, message } = useApiResource<CapabilityDetailResponse>(
    token,
    repo,
    `/capabilities/${encodeURIComponent(id)}`,
  );

  const backLink = (
    <a
      href={repoPath(repo, "capabilities")}
      onClick={(e) => {
        e.preventDefault();
        navigate(repoPath(repo, "capabilities"));
      }}
    >
      ← Back to register
    </a>
  );

  if (status === "loading") {
    return (
      <section className="panel">
        {backLink}
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready" || !response) {
    return (
      <section className="panel">
        {backLink}
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const data = response.capability;
  const registerSync = response.registerSync;
  const roles = data.roles ?? [];
  const adrReferences = data.adrReferences ?? [];
  const linkedAdrs = data.linkedAdrs ?? [];
  const relatedDesigns = data.relatedDesigns ?? [];
  const relatedRunbooks = data.relatedRunbooks ?? [];

  return (
    <section className="panel capability-detail">
      {backLink}
      <h2>{data.name}</h2>
      <p className="mono muted-note">{data.id}</p>
      <p className={registerSync.stale ? "sync-note err" : "sync-note"}>
        {registerSync.fetchedAt ? `Register last fetched ${new Date(registerSync.fetchedAt).toLocaleString()}` : "Register not yet fetched"}
        {registerSync.stale && " — the latest refresh failed; showing the last known-good data."}
      </p>

      <dl className="stat-grid">
        <div>
          <dt>Status</dt>
          <dd>{data.issueState}</dd>
        </div>
        <div>
          <dt>Plane</dt>
          <dd>{data.plane}</dd>
        </div>
        <div>
          <dt>Phase</dt>
          <dd>{data.snapshot.phase || "—"}</dd>
        </div>
        <div>
          <dt>Criticality</dt>
          <dd>{data.roleCriticality}</dd>
        </div>
      </dl>
      <p className="muted-note">
        Phase is frozen as of {data.snapshot.date} — a roadmap fact that can go stale when replanning happens. Status and
        Criticality are live.
      </p>

      <p>
        <a href={data.issueUrl} target="_blank" rel="noopener noreferrer">
          View source issue{data.issueNumber ? ` #${data.issueNumber}` : ""}
        </a>{" "}
        — current status, evidence, and any corrections live there, not on this page.
        {data.commentCount > 0 && ` ${data.commentCount} comment${data.commentCount === 1 ? "" : "s"}.`}
      </p>

      <dl className="stat-grid">
        <div>
          <dt>Future surface</dt>
          <dd>{data.snapshot.futureSurface || "—"}</dd>
        </div>
        <div>
          <dt>Gate B function</dt>
          <dd>{data.snapshot.gateBFunction || data.gateBLabel || "—"}</dd>
        </div>
      </dl>
      <p className="muted-note">Frozen as of {data.snapshot.date} — see the source issue for anything more current.</p>

      <h3>Applicable roles</h3>
      {roles.length > 0 ? (
        <ul>
          {roles.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      ) : (
        <p className="muted-note">None recorded.</p>
      )}

      <h3 id="linked-adrs">Linked ADRs</h3>
      {linkedAdrs.length > 0 ? (
        <ul className="adr-list">
          {linkedAdrs.map((a) => (
            <li key={a.id}>
              <AdrLink id={a.id} title={a.title} />
              <span>{a.title || <span className="muted-note">(unresolved reference)</span>}</span>
              {a.status && <span className="muted-note">{a.status}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted-note">
          {adrReferences.length > 0 ? "None of the declared references resolved." : "No governing ADR recorded."}
        </p>
      )}

      {/* Spec's "Evidence graph" (Capability detail page section): a
          compact overview of the same relationships detailed below, not a
          second copy of them — each edge jumps to its full section rather
          than repeating titles (avoids two renderings of the same item
          disagreeing if one is ever trimmed independently). The B&IT edge
          ("illustrated by") is omitted — no groundable signal exists for
          B&IT items, see internal/gallery's package doc comment. The
          "evidenced by → source file or test" edge is also omitted since
          the 2026-08-27 migration: evidence is volatile (frozen forever,
          never re-verified) and no longer part of this page's data at all
          — see the source-issue link above instead. */}
      <section className="evidence-graph" role="region" aria-label="Evidence graph">
        <h3>Evidence graph</h3>
        <ul className="evidence-graph-tree">
          <li>
            <span className="edge-label">governed by → ADR</span>{" "}
            {linkedAdrs.length > 0 ? (
              <a href="#linked-adrs">
                {linkedAdrs.length} ADR{linkedAdrs.length === 1 ? "" : "s"}
              </a>
            ) : (
              <span className="muted-note">None</span>
            )}
          </li>
          <li>
            <span className="edge-label">explained by → design document</span>{" "}
            {relatedDesigns.length > 0 ? (
              <a href="#related-designs">
                {relatedDesigns.length} design document{relatedDesigns.length === 1 ? "" : "s"}
              </a>
            ) : (
              <span className="muted-note">None</span>
            )}
          </li>
          <li>
            <span className="edge-label">operated by → runbook</span>{" "}
            {relatedRunbooks.length > 0 ? (
              <a href="#related-runbooks">
                {relatedRunbooks.length} runbook{relatedRunbooks.length === 1 ? "" : "s"}
              </a>
            ) : (
              <span className="muted-note">None</span>
            )}
          </li>
        </ul>
      </section>

      <h3 id="related-designs">Related designs</h3>
      {relatedDesigns.length > 0 ? (
        <ul>
          {relatedDesigns.map((d) => (
            <li key={d.sourcePath}>
              {d.title} <span className="muted-note">— related via {(d.sharedAdrs ?? []).join(", ")}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted-note">None found.</p>
      )}

      <h3 id="related-runbooks">Related runbooks</h3>
      {relatedRunbooks.length > 0 ? (
        <ul>
          {relatedRunbooks.map((r) => (
            <li key={r.id}>
              <a
                href={repoPath(repo, "runbooks", r.id)}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(repoPath(repo, "runbooks", r.id));
                }}
              >
                {r.title}
              </a>{" "}
              <span className="muted-note">— related via {(r.sharedAdrs ?? []).join(", ")}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted-note">None found.</p>
      )}
    </section>
  );
}
